import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { urlToBase64 } from '@/utils';
import { ReportHeaderProps } from '../../ReportContainers/types';
import { PictureAsPdf } from '@mui/icons-material';
import { abyssColors } from '@/theme/colors';
import { CircularProgress, Tooltip } from '@mui/material';

const createPdfLink = (blob: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const ExportPaintBlockReportButton = ({
  chaseItemsData,
  blockOverViewData,
  actualValuesData,
  remarksData,
}: ReportHeaderProps) => {
  const { showSnackBar } = useSnackBarMessage({});
  const [loading, setLoading] = useState(false);
  const selectedReport = useAtomValue(state.selectedReport);

  const worker = useMemo(
    () => new Worker(new URL('paintBlockReportWorker.tsx', import.meta.url), { type: 'module' }),
    []
  );

  useEffect(() => {
    return () => worker.terminate();
  }, [worker]);

  const generatePdf = useCallback(async () => {
    setLoading(true);

    const promises = selectedReport?.files?.map((file) =>
      urlToBase64(getCloudfrontUrl(file.resourcePath), true)
    );

    const base64IReportmages = await Promise.all(promises || []);
    const chaseItemsWithoutId = chaseItemsData
      ? {
          columns: chaseItemsData.columns,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          rows: chaseItemsData.rows?.map(({ id, ...rest }) => rest) || [],
        }
      : { columns: [], rows: [] };

    const pdfBlobPromise = new Promise((resolve, reject) => {
      worker.addEventListener('message', (event) => resolve(event.data));
      worker.addEventListener('error', (event) => reject(event.message));
      worker.postMessage({
        props: {
          chaseItemsData: chaseItemsWithoutId,
          blockOverViewData: blockOverViewData,
          actualValuesData: actualValuesData,
          remarksData: remarksData,
          report: selectedReport,
          images: base64IReportmages,
        },
      });
    });
    try {
      const pdfBlob = await pdfBlobPromise;
      createPdfLink(pdfBlob as Blob, `${selectedReport?.name}.pdf`);
      setLoading(false);
    } catch (error) {
      console.log(error);

      const message = extractErrorMessageFromUnknownError(error);
      showSnackBar(message || 'An error occurred', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    selectedReport,
    worker,
    chaseItemsData,
    blockOverViewData,
    actualValuesData,
    remarksData,
    showSnackBar,
  ]);

  return (
    <>
      {loading ? (
        <CircularProgress
          sx={{ color: abyssColors.primary[500] }}
          size={16}
          data-testid="generate-pdf-loading"
        />
      ) : (
        <Tooltip title="Please stay on this page until the PDF generates">
          <PictureAsPdf
            data-testid="export-paintblock-report-icon"
            htmlColor={abyssColors.primary[500]}
            sx={{ cursor: 'pointer' }}
            onClick={generatePdf}
          />
        </Tooltip>
      )}
    </>
  );
};
